import React, { useState, useEffect, useRef } from 'react';
import NetworkLegend from './NetworkLegend';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Core } from 'cytoscape';
import { NetworkData, NodeType } from '@/features/connect-dots/types/cytoscape';

import { useTranslation } from 'react-i18next';
import { rwColors } from '@/styles/colors';

type ToolbarProps = {
  searchText: string;
  setSearchText: (v: string) => void;
  handleNodeSearch: () => void;
  cytoscapeCoreRefObject: React.MutableRefObject<Core | null>;
  selectedThemeId: string | undefined;
  setSelectedThemeId: (id: string) => void;
  filteredData: NetworkData | null;
  nodeTypeFilters: { [key in NodeType]: boolean };
  updateNodeTypeFilter: (nodeType: NodeType, isVisible: boolean) => void;
};

export function Toolbar({
  searchText,
  setSearchText,
  handleNodeSearch,
  cytoscapeCoreRefObject,
  selectedThemeId,
  setSelectedThemeId,
  filteredData,
  nodeTypeFilters,
  updateNodeTypeFilter,
}: ToolbarProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const themeLabelOptions = (filteredData?.nodes ?? [])
    .filter((n) => n.nodeType === NodeType.THEME)
    .map((n) => {
      return (
        <option key={n.id} value={n.id}>
          {n.label ?? t('未設定')}
        </option>
      );
    })
    .filter(Boolean) as JSX.Element[];

  themeLabelOptions?.sort((a, b) => {
    const labelA = a.props.children.toLowerCase();
    const labelB = b.props.children.toLowerCase();
    return labelA.localeCompare(labelB);
  });

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  // メニュー外クリックを検出して閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!cytoscapeCoreRefObject) {
    return null; // Cytoscapeが初期化されていない場合は何も表示しない
  }

  return (
    <div className="my-2 ml-2 flex items-center gap-4">
      {/* 検索ボックス*/}
      <input
        type="text"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder={t('キーワードを検索（部分一致）')}
        className="w-96 rounded border px-2 py-1 text-sm"
        onKeyDown={async (e) => {
          if (e.key === 'Enter') {
            await handleNodeSearch();
          }
        }}
      />
      <button
        onClick={handleNodeSearch}
        className="rounded px-2 py-1 text-sm text-white"
        style={{ backgroundColor: rwColors.sub }}
      >
        <MagnifyingGlassIcon className="size-4" />
      </button>

      <div className="relative bg-white text-sm" ref={menuRef}>
        {/* 開閉ボタンとタイトル */}
        <div
          className="flex cursor-pointer items-center justify-between rounded p-1 hover:bg-gray-200"
          onClick={toggleOpen}
        >
          <h3 className="text-xs font-semibold text-gray-700">
            {t('表示設定')}...
          </h3>
        </div>

        {/* 開閉されるコンテンツ */}
        {isOpen && (
          <div className="absolute left-0 top-full z-10 mt-1 w-64 rounded border bg-white text-sm text-gray-700">
            <NetworkLegend
              nodeCount={filteredData?.nodes.length ?? 0}
              nodeTypeFilters={nodeTypeFilters}
              updateNodeTypeFilter={updateNodeTypeFilter}
            />
          </div>
        )}
      </div>

      <select
        className="ml-auto rounded border px-2 py-1 text-sm"
        value={selectedThemeId}
        onChange={(e) => {
          setSelectedThemeId(e.target.value);
          if (cytoscapeCoreRefObject.current && e.target.value) {
            const target = cytoscapeCoreRefObject.current.getElementById(
              e.target.value
            );
            if (target && target.length > 0) {
              cytoscapeCoreRefObject.current.animate({
                center: { eles: target },
                duration: 600,
                easing: 'ease-in-out',
                zoom: 0.8,
              });
            }
          }
        }}
      >
        <option value="">{t('テーマへ移動...')}</option>
        {themeLabelOptions}
      </select>
    </div>
  );
}
